import { format, isValid, parseISO } from 'date-fns';

export interface InputFormatter<T> {
  /** Source value -> display string (e.g. date -> "yyyy-MM-dd'T'HH:mm"). Nullish renders empty. */
  format: (value: T | null | undefined) => string;
  /** Display string -> committed value (e.g. Number, parseISO, trim), or `null` when unparseable. */
  parse: (draft: string) => T | null;
}

/**
 * Identity formatter used as the default: stringifies the source value (nullish
 * renders empty) and passes the draft straight through.
 */
export const stringFormatter: InputFormatter<string> = {
  format: (value) => (value == null ? '' : String(value)),
  parse: (draft) => draft,
};

/**
 * Number field that leaves the value unset when cleared or unparseable. Empty or
 * non-numeric input commits `null` (instead of silently committing 0 / NaN); the
 * raw string is kept while typing, so entries like "1." or leading zeros don't get
 * rewritten under the caret.
 */
export const numberFormatter: InputFormatter<number> = {
  format: (value) => (value == null || Number.isNaN(value) ? '' : String(value)),
  parse: (draft) => {
    if (draft.trim() === '') return null;
    const parsed = Number(draft);
    return Number.isNaN(parsed) ? null : parsed;
  },
};

/**
 * `<input type="datetime-local">` <-> Date. Blank or unparseable input commits
 * `null`; an invalid Date formats to an empty string.
 */
export const dateTimeLocalFormatter: InputFormatter<Date> = {
  format: (value) => (value && isValid(value) ? format(value, "yyyy-MM-dd'T'HH:mm") : ''),
  parse: (draft) => {
    if (!draft) return null;
    const parsed = parseISO(draft);
    return isValid(parsed) ? parsed : null;
  },
};
